import type { PayloadAction, Slice } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { Customer } from 'types/customer';
import { InvoiceInfo } from 'types/invoiceInfo';
import { Location } from 'types/location';
import { Payer, SplitOption } from 'types/payer';
import {
  DamageClaim,
  InsurancePolicies,
  InsurancePolicy,
} from '@services/payersApi';
import { DateTime } from 'luxon';
import { fetchInitialUserData } from '@services/authApi';
import { getPrefixByIsoCode } from '@utils/countries';
import { Car, CarGroup, Equipment } from '../../types/car';
import { Price } from '../../types/priceDetail';
import { CustomerInsurance } from '../../types/customerInsurance';
import { StationsResponse } from '@services/stationsApi';
import { Station, Stations } from '../../types/station';

export interface CustomerInfo {
  horsepower: number;
  model: string;
  year: string;
  group: string;
  organizationNumber: string;
  name: string;
  firstName: string;
  lastName: string;
  phone: string;
  mobile: string;
  address: string;
  city: string;
  zip: string;
  carLicenseNumber: string;
  brandText: string;
}

export interface StationOpeningHours {
  [key: string]: {
    [key: string]: number;
  } | null;
}

export interface UpdateReservationState {
  id?: string;
  number?: number;
  pickupDate: number;
  returnDate: number;
  comments?: string;
  car?: Car;
  customer: Customer;
  carGroup: CarGroup;
  price?: Price;
  payer?: Payer;
  invoiceInfo?: InvoiceInfo;
  pickupLocation: Location;
  returnLocation: Location;
  equipment?: Equipment;
  editable?: boolean;
  deletable?: boolean;
  cdw?: CustomerInsurance;
  scw?: CustomerInsurance;
  pai?: CustomerInsurance;
  tp?: CustomerInsurance;
  onlineCheckin?: boolean;
  onlineCheckinDone?: boolean;
  key?: string;
  duplicates?: null | number;
  updateOnlineCheckin?: boolean;
  reservation?: boolean;
  out?: boolean;
  noshow?: boolean;
  updateMode?: boolean;
}

export interface ReservationState {
  step: number;
  activeReservation: UpdateReservationState;
  reservation: {
    pickupStationEarliestOpeningHours: StationOpeningHours;
    returnStationEarliestOpeningHours: StationOpeningHours;
    pickupStationOpeningHours: StationOpeningHours;
    returnStationOpeningHours: StationOpeningHours;
    stations: Stations;
    pickupLocation: Location;
    returnLocation: Location;
    invoiceInfo?: InvoiceInfo;
    customer?: Customer;
    pickupDate: number;
    returnDate: number;
    planCar?: {
      pickupDate: number;
      returnDate: number;
      carGroup: CarGroup;
      selectedCar: Car;
    };
    comments?: string;
    hasEmail: boolean;
    phonePrefix?: string;
    phoneIsoCode?: string;
    phoneNumber?: string;
    bisnodeId1?: string;
    bisnodeSearchResultIsNull?: boolean | null;
    bisnodePhoneNumber?: string;
    bisnodePhoneIsoCode?: string;
    bisnodePhonePrefix?: string;
    payer?: Payer;
  };
  carGroup: CarGroup;
  insurance_policies?: InsurancePolicies;
  selected_insurance_policy?: {
    insurance: InsurancePolicy;
    damages: DamageClaim[];
  };
  dayDifference: number;
  bisnodeCustomerInfo: CustomerInfo;
  selected_damage_claim?: DamageClaim;
  isManualPickupAddressChecked?: boolean;
  isManualReturnAddressChecked?: boolean;
  selectedVoucherSplitOption: SplitOption | null;
  touched: Record<string, boolean>;
  errors: Record<string, boolean | string>;
  resetStatus: boolean;
  extraSearchValue: string;
  isReturnDateError: boolean;
  isPickUpDateError: boolean;
  pickupDateErrorCode: string;
  returnDateErrorCode: string;
  stationClosingTime: number | null;
  stationOpeningTime: number | null;
  reservationHasCar: boolean;
}

export const initialState: ReservationState = {
  step: 0,
  activeReservation: null,
  extraSearchValue: '',
  reservation: {
    pickupStationOpeningHours: null,
    returnStationOpeningHours: null,
    pickupStationEarliestOpeningHours: null,
    returnStationEarliestOpeningHours: null,
    stations: null,
    pickupLocation: {
      station: null,
      address: null,
      pickupPlace: null,
    },
    returnLocation: {
      station: null,
      address: null,
      pickupPlace: null,
    },
    comments: '',
    payer: null,
    pickupDate: null,
    returnDate: null,
    invoiceInfo: {
      invoiceRecipient: '',
      customerCarLicenseNumber: '',
      damageNumber: '',
      invoiceReference: '',
      invoiceText: '',
    },
    planCar: {
      pickupDate: DateTime.now().toMillis(),
      returnDate: DateTime.now().plus({ days: 1 }).toMillis(),
      carGroup: {
        name: '',
        description: '',
        acrissCode: '',
        imageUrl: '',
      },
      selectedCar: null,
    },
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      licenseNumber: '',
      country: '',
      address: '',
      zip: '',
      city: '',
    },
    phoneNumber: '',
    phonePrefix: '',
    phoneIsoCode: '',
    hasEmail: true,
    bisnodeId1: '',
    bisnodeSearchResultIsNull: null,
    bisnodePhoneNumber: '',
    bisnodePhoneIsoCode: '',
    bisnodePhonePrefix: '',
  },
  carGroup: null,
  insurance_policies: null,
  selected_insurance_policy: null,
  selected_damage_claim: null,
  dayDifference: 1,
  bisnodeCustomerInfo: {
    horsepower: 0,
    address: '',
    brandText: '',
    carLicenseNumber: '',
    city: '',
    firstName: '',
    group: '',
    lastName: '',
    mobile: '',
    model: '',
    name: '',
    organizationNumber: '',
    phone: '',
    year: '',
    zip: '',
  },
  isManualPickupAddressChecked: false,
  isManualReturnAddressChecked: false,
  selectedVoucherSplitOption: null,
  isReturnDateError: false,
  isPickUpDateError: false,
  pickupDateErrorCode: '',
  returnDateErrorCode: '',
  stationClosingTime: null,
  stationOpeningTime: null,
  reservationHasCar: false,
  touched: {},
  errors: {},
  resetStatus: false,
};

const reservationSlice: Slice<ReservationState> = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setReservationHasCar: (state, action: PayloadAction<boolean>) => {
      state.reservationHasCar = action.payload;
    },
    setStationClosingTime: (state, action: PayloadAction<number | null>) => {
      state.stationClosingTime = action.payload;
    },
    setStationOpeningTime: (state, action: PayloadAction<number | null>) => {
      state.stationOpeningTime = action.payload;
    },
    setReturnDateErrorCode: (state, action: PayloadAction<string>) => {
      state.returnDateErrorCode = action.payload;
    },
    setPickupDateErrorCode: (state, action: PayloadAction<string>) => {
      state.pickupDateErrorCode = action.payload;
    },
    setIsReturnDateError: (state, action: PayloadAction<boolean>) => {
      state.isReturnDateError = action.payload;
    },
    setIsPickupDateError: (state, action: PayloadAction<boolean>) => {
      state.isPickUpDateError = action.payload;
    },
    setExtraSearchValue: (state, action: PayloadAction<string>) => {
      state.extraSearchValue = action.payload;
    },
    setIsReturnManualAddressCheck: (state, action: PayloadAction<boolean>) => {
      state.isManualReturnAddressChecked = action.payload;
    },
    setIsPickupManualAddressCheck: (state, action: PayloadAction<boolean>) => {
      state.isManualPickupAddressChecked = action.payload;
    },
    setSelectedVoucherSplitOption: (
      state,
      action: PayloadAction<SplitOption>
    ) => {
      state.reservation.payer.selectedVoucherSplitOption = action.payload;
    },
    setPickupStationEarliestOpeningHours: (
      state,
      action: PayloadAction<StationOpeningHours>
    ) => {
      state.reservation.pickupStationEarliestOpeningHours = {
        ...action.payload,
      };
    },
    setReturnStationEarliestOpeningHours: (
      state,
      action: PayloadAction<StationOpeningHours>
    ) => {
      state.reservation.returnStationEarliestOpeningHours = {
        ...action.payload,
      };
    },
    setPickupStationOpeningHours: (
      state,
      action: PayloadAction<StationOpeningHours>
    ) => {
      state.reservation.pickupStationOpeningHours = {
        ...action.payload,
      };
    },
    setReturnStationOpeningHours: (
      state,
      action: PayloadAction<StationOpeningHours>
    ) => {
      state.reservation.returnStationOpeningHours = {
        ...action.payload,
      };
    },
    setInvoiceReference: (state, action: PayloadAction<string>) => {
      state.reservation.invoiceInfo.invoiceReference = action.payload;
    },
    setComment: (state, action: PayloadAction<string>) => {
      state.reservation.comments = action.payload;
    },
    setPayer: (state, action: PayloadAction<Payer>) => {
      state.reservation.payer = action.payload;
    },
    setCarLicenseNumber: (state, action: PayloadAction<string>) => {
      state.reservation.invoiceInfo.customerCarLicenseNumber = action.payload;
    },
    setDamageNumber: (state, action: PayloadAction<string>) => {
      state.reservation.invoiceInfo.damageNumber = action.payload;
    },
    setCarGroup: (state, action: PayloadAction<CarGroup>) => {
      state.carGroup = action.payload;
    },
    setPlanCar: (state, action: PayloadAction<CarGroup>) => {
      state.reservation.planCar = {
        ...state.reservation.planCar,
        ...action.payload,
      };
    },
    setPlanCarCarGroup: (state, action: PayloadAction<CarGroup>) => {
      state.reservation.planCar.carGroup = {
        ...state.reservation.planCar.carGroup,
        ...action.payload,
      };
    },
    setReservation: (
      state,
      action: PayloadAction<ReservationState['reservation']>
    ) => {
      state.reservation = { ...state.reservation, ...action.payload };
      state.reservation.planCar.pickupDate = action.payload.pickupDate;
    },
    setActiveReservation: (
      state,
      action: PayloadAction<ReservationState['reservation']>
    ) => {
      state.activeReservation = {
        ...state.activeReservation,
        ...action.payload,
      };
    },
    setCustomerFirstName: (state, action: PayloadAction<string>) => {
      state.reservation.customer.firstName = action.payload;
    },
    setCustomerLastName: (state, action: PayloadAction<string>) => {
      state.reservation.customer.lastName = action.payload;
    },
    setCustomerAddress: (state, action: PayloadAction<string>) => {
      state.reservation.customer.address = action.payload;
    },
    setCustomerZip: (state, action: PayloadAction<string>) => {
      state.reservation.customer.zip = action.payload;
    },
    setCustomerCity: (state, action: PayloadAction<string>) => {
      state.reservation.customer.city = action.payload;
    },
    setCustomerPhonePrefix: (state, action: PayloadAction<string>) => {
      state.reservation.phonePrefix = action.payload;
    },
    setCustomerPhoneIsoCode: (state, action: PayloadAction<string>) => {
      state.reservation.phoneIsoCode = action.payload;
    },
    setCustomerPhoneNumber: (state, action: PayloadAction<string>) => {
      state.reservation.phoneNumber = action.payload;
    },
    setCustomerLicenseNumber: (state, action: PayloadAction<string>) => {
      state.reservation.customer.licenseNumber = action.payload;
    },
    setCustomerLicenseNumberExpire: (state, action: PayloadAction<string>) => {
      state.reservation.customer.licenseExpire = action.payload;
    },
    setBisnodeCustomerInfo: (state, action: PayloadAction<CustomerInfo>) => {
      state.bisnodeCustomerInfo = action.payload;
    },
    setBisnodeId1: (state, action: PayloadAction<string>) => {
      state.reservation.bisnodeId1 = action.payload;
    },
    setBisnodeSearchResultIsNull: (state, action: PayloadAction<boolean>) => {
      state.reservation.bisnodeSearchResultIsNull = action.payload;
    },
    setBisnodePhoneNumber: (state, action: PayloadAction<string>) => {
      state.reservation.bisnodePhoneNumber = action.payload;
    },
    setBisnodePhoneIsoCode: (state, action: PayloadAction<string>) => {
      state.reservation.bisnodePhoneIsoCode = action.payload;
    },
    setBisnodePhonePrefix: (state, action: PayloadAction<string>) => {
      state.reservation.bisnodePhonePrefix = action.payload;
    },
    setInsurancePolicies: (state, action: PayloadAction<InsurancePolicies>) => {
      state.insurance_policies = action.payload;
    },
    setHasEmail: (state, action: PayloadAction<boolean>) => {
      state.reservation.hasEmail = action.payload;
    },
    setCustomerEmail: (state, action: PayloadAction<string>) => {
      state.reservation.customer.email = action.payload;
    },
    setSelectedInsurancePolicy: (
      state,
      action: PayloadAction<{
        insurance: InsurancePolicy;
        damages: DamageClaim[];
      }>
    ) => {
      state.selected_insurance_policy = action.payload;
    },
    setCustomerCountry: (state, action: PayloadAction<string>) => {
      state.reservation.customer.country = action.payload;
    },
    setSelectedDamageClaim: (state, action: PayloadAction<DamageClaim>) => {
      state.selected_damage_claim = action.payload;
    },
    setPickupDate: (state, action: PayloadAction<number>) => {
      state.reservation.planCar.pickupDate = action.payload;
    },
    setReturnDate: (state, action: PayloadAction<number>) => {
      state.reservation.planCar.returnDate = action.payload;
    },
    setPickupLocation: (state, action: PayloadAction<Location>) => {
      state.reservation.pickupLocation = action.payload;
    },
    setPickupStation: (state, action: PayloadAction<Station>) => {
      state.reservation.pickupLocation.station = action.payload;
    },
    setPickupPlace: (
      state,
      action: PayloadAction<{ id: string; name: string }>
    ) => {
      if (action.payload === null) {
        delete state.reservation.pickupLocation.pickupPlace;
      } else {
        state.reservation.pickupLocation.pickupPlace = action.payload;
      }
    },
    setPickupAddress: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.reservation.pickupLocation.address = action.payload;
    },
    setReturnLocation: (state, action: PayloadAction<Location>) => {
      state.reservation.returnLocation = action.payload;
    },
    setReturnStation: (state, action: PayloadAction<Station>) => {
      state.reservation.returnLocation.station = action.payload;
    },
    setReturnPlace: (
      state,
      action: PayloadAction<{ id: string; name: string }>
    ) => {
      state.reservation.returnLocation.pickupPlace = action.payload;
    },
    setReturnAddress: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      if (action.payload === null) {
        delete state.reservation.returnLocation.address;
      } else {
        state.reservation.returnLocation.address = action.payload;
      }
    },
    setStations: (state, action: PayloadAction<StationsResponse>) => {
      state.reservation.stations = action.payload;
    },
    setSelectedCar: (state, action: PayloadAction<Car>) => {
      state.reservation.planCar.selectedCar = action.payload;
    },
    setDayDifference: (state, action: PayloadAction<number>) => {
      state.dayDifference = action.payload;
    },
    setTouched: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.touched = { ...state.touched, ...action.payload };
    },
    setErrors: (
      state,
      action: PayloadAction<Record<string, string | boolean>>
    ) => {
      state.errors = { ...state.errors, ...action.payload };
    },
    resetReservation: (state) => {
      return {
        ...initialState,

        resetStatus: !state.resetStatus,
      };
    },
    resetPayer: (state) => {
      state.reservation.payer = { ...initialState.reservation.payer };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialUserData.fulfilled, (state, action) => {
      state.reservation.customer.country =
        action.payload.country.name.charAt(0).toUpperCase() +
        action.payload.country.name.slice(1).toLowerCase();
      state.reservation.phonePrefix = getPrefixByIsoCode(
        action.payload.country.isocode
      );
      state.reservation.phoneIsoCode =
        action.payload.country.isocode.toUpperCase();
    });
  },
});

export const {
  setStep,
  setReservationHasCar,
  setReturnDateErrorCode,
  setPickupDateErrorCode,
  setStationClosingTime,
  setStationOpeningTime,
  setIsReturnDateError,
  setIsPickupDateError,
  setReservation,
  setActiveReservation,
  setComment,
  setPayer,
  resetReservation,
  resetPayer,
  setSelectedInsurancePolicy,
  setSelectedDamageClaim,
  setInsurancePolicies,
  setTouched,
  setErrors,
  setInvoiceReference,
  setSelectedVoucherSplitOption,
  setCarLicenseNumber,
  setCustomerLicenseNumber,
  setCustomerLicenseNumberExpire,
  setDamageNumber,
  setCarGroup,
  setPlanCar,
  setPlanCarCarGroup,
  setPickupDate,
  setPickupAddress,
  setReturnDate,
  setDayDifference,
  setCustomerCountry,
  setHasEmail,
  setCustomerEmail,
  setCustomerFirstName,
  setCustomerLastName,
  setCustomerAddress,
  setCustomerZip,
  setCustomerCity,
  setCustomerPhonePrefix,
  setCustomerPhoneIsoCode,
  setCustomerPhoneNumber,
  setBisnodeId1,
  setBisnodeSearchResultIsNull,
  setBisnodePhoneNumber,
  setExtraSearchValue,
  setBisnodePhoneIsoCode,
  setBisnodePhonePrefix,
  setPickupLocation,
  setPickupStation,
  setReturnLocation,
  setReturnAddress,
  setPickupPlace,
  setReturnPlace,
  setReturnStation,
  setStations,
  setSelectedCar,
  setPickupStationOpeningHours,
  setReturnStationOpeningHours,
  setPickupStationEarliestOpeningHours,
  setReturnStationEarliestOpeningHours,
  setBisnodeCustomerInfo,
  setIsPickupManualAddressCheck,
  setIsReturnManualAddressCheck,
} = reservationSlice.actions;
export default reservationSlice.reducer;
